import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { BellIcon } from '../icons.jsx';

/**
 * Top-bar notification bell (UI_FRAMEWORK Q4 resolution).
 * Shell ready in v1: UI is built, backend `GET /api/notifications` returns [].
 * Click opens a Popover with empty-state message. Badge hidden while count=0.
 *
 * Post-v1 enablement: swap the empty array for real DB reads; add "Mark as read"
 * interactions + real-time polling/SSE. Zero frontend re-architecture needed.
 */
export function NotificationBell() {
  const { data = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        return await api.get('/api/notifications');
      } catch {
        return []; // endpoint not wired yet in Phase 4.0 — empty array is a safe default
      }
    },
    staleTime: 60_000,
  });
  const count = data.length;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative flex h-10 w-10 items-center justify-center rounded-md text-ink-500 transition-colors hover:bg-sky-100 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1"
          aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        >
          <BellIcon className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-blue-600" />
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          sideOffset={8}
          align="end"
          className="z-50 w-80 rounded-md border border-sky-300 bg-white p-4 shadow-md focus:outline-none"
        >
          <h3 className="mb-2 text-sm font-semibold text-ink-900">Notifications</h3>
          {count === 0 ? (
            <p className="text-sm text-ink-500">No notifications yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.map((n) => (
                <li key={n.id} className="text-sm text-ink-600">
                  {n.message}
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
